import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../components/base/Button";
import SEO from "../../components/base/SEO";
import Footer from "../../components/feature/Footer";
import Header from "../../components/feature/Header";

const moduleThemes = {
  sunset: {
    chip: "bg-rose-100 text-rose-700",
    border: "border-rose-200",
    ring: "ring-rose-500",
    panel: "from-rose-500 via-orange-500 to-amber-400",
    soft: "bg-rose-50",
    text: "text-rose-700",
    glow: "shadow-rose-200/60",
  },
  amber: {
    chip: "bg-amber-100 text-amber-700",
    border: "border-amber-200",
    ring: "ring-amber-500",
    panel: "from-amber-500 via-orange-500 to-yellow-400",
    soft: "bg-amber-50",
    text: "text-amber-700",
    glow: "shadow-amber-200/60",
  },
  sky: {
    chip: "bg-sky-100 text-sky-700",
    border: "border-sky-200",
    ring: "ring-sky-500",
    panel: "from-sky-500 via-cyan-500 to-teal-400",
    soft: "bg-sky-50",
    text: "text-sky-700",
    glow: "shadow-sky-200/60",
  },
  emerald: {
    chip: "bg-emerald-100 text-emerald-700",
    border: "border-emerald-200",
    ring: "ring-emerald-500",
    panel: "from-emerald-500 via-teal-500 to-cyan-400",
    soft: "bg-emerald-50",
    text: "text-emerald-700",
    glow: "shadow-emerald-200/60",
  },
  violet: {
    chip: "bg-violet-100 text-violet-700",
    border: "border-violet-200",
    ring: "ring-violet-500",
    panel: "from-violet-500 via-fuchsia-500 to-rose-400",
    soft: "bg-violet-50",
    text: "text-violet-700",
    glow: "shadow-violet-200/60",
  },
} as const;

type ERPModule = {
  id: string;
  label: string;
  short: string;
  title: string;
  description: string;
  bullets: string[];
  sopFit: string[];
  icon: string;
  accent: keyof typeof moduleThemes;
};

const erpModules: readonly ERPModule[] = [
  {
    id: "finance",
    label: "財務會計",
    short: "應收、應付、總帳與現金流集中管理",
    title: "當日常收付款與對數回到同一套資料，財務先會真正清楚",
    description:
      "如果你公司現時要在 Excel、網銀、發票和會計工具之間來回對數，財務模組就是把這些日常動作重新串起來，讓收款、付款、發票和分錄對得上。",
    bullets: [
      "整合應收帳、應付帳、日常費用與基礎會計資料",
      "可配合 FPS reference、發票編號與收款對數習慣",
      "讓管理層更早看到欠款、現金流與部門成本狀態",
    ],
    sopFit: [
      "收款 reference 對回發票",
      "付款條件按客戶 / 供應商設定",
      "完成出貨後推進應收跟進",
    ],
    icon: "ri-bank-card-line",
    accent: "sunset",
  },
  {
    id: "purchasing",
    label: "採購管理",
    short: "由請購、採購到收貨，按公司節奏走",
    title: "把你公司現有補貨和採購習慣放回同一條可追蹤流程",
    description:
      "採購模組不只是開採購單，而是把請購、報價、批核、到貨和成本一起管理，讓每次補貨或採購都不再只靠同事經驗與口頭溝通。",
    bullets: [
      "支援請購、採購單、收貨單與供應商資料整理",
      "可按庫存水平或內部需求建立採購節點",
      "按金額、角色或部門走不同批核方式",
    ],
    sopFit: [
      "庫存不足先請購再採購",
      "大額採購要主管批核",
      "到貨後自動回寫庫存",
    ],
    icon: "ri-shopping-bag-3-line",
    accent: "amber",
  },
  {
    id: "sales",
    label: "銷售管理",
    short: "報價、訂單、出貨與收款一條線處理",
    title: "把業務、營運與財務看同一份客戶交易脈絡",
    description:
      "如果報價由業務做、訂單由營運跟、收款由財務追，銷售模組就是把這幾步接回同一流程，減少重覆輸入和部門之間資料落差。",
    bullets: [
      "報價、銷售單、送貨單、發票與收款可連接處理",
      "不同客戶可設定價格、信用額與付款條件",
      "清楚追蹤未完成訂單、部分出貨與欠款狀況",
    ],
    sopFit: [
      "報價批核後自動轉銷售單",
      "不同客戶走不同付款條件",
      "出貨後由財務接手跟進收款",
    ],
    icon: "ri-file-chart-line",
    accent: "sky",
  },
  {
    id: "inventory",
    label: "庫存管理",
    short: "存量、貨位、批次與調撥回到同一畫面",
    title: "當公司開始唔只問『有冇貨』，庫存流程就要清楚起來",
    description:
      "庫存模組適合已經開始面對多倉、批次、調撥或盤點問題的公司。它會把收貨、上架、出貨、調撥和盤點整合成更穩定的日常 SOP。",
    bullets: [
      "支援多倉、多貨位、批次與安全庫存設定",
      "處理調撥、盤點、報損與狀態庫存",
      "與採購、銷售及出入庫節點同步更新",
    ],
    sopFit: [
      "收貨後即更新可用量",
      "低於安全庫存觸發補貨",
      "多倉調撥按公司規則處理",
    ],
    icon: "ri-stack-line",
    accent: "emerald",
  },
  {
    id: "orders",
    label: "訂單管理",
    short: "跨部門訂單狀態集中追蹤",
    title: "當一張單牽涉幾個部門，最需要的是進度清楚而不是更多表格",
    description:
      "訂單模組把待處理、待出貨、待收款和異常節點集中起來，讓管理層和同事不用逐個部門追問，已經知道單卡在哪一步。",
    bullets: [
      "統一追蹤待處理、待出貨、待收款與異常訂單",
      "可配合不同角色查看不同節點與備註",
      "支援部分出貨、附件、批核與狀態通知",
    ],
    sopFit: [
      "按部門拆開處理責任",
      "部分出貨後保留餘下狀態",
      "異常訂單集中管理",
    ],
    icon: "ri-survey-line",
    accent: "violet",
  },
  {
    id: "crm",
    label: "客戶 CRM",
    short: "客戶資料、聯絡紀錄與交易歷史集中管理",
    title: "當客戶資料回到同一張卡上，前線回應才會更快更準",
    description:
      "CRM 模組不是獨立另一套系統，而是讓報價、訂單、付款條件、聯絡紀錄和交易表現都回到同一份客戶資料，減少部門各有一份舊版本。",
    bullets: [
      "整合客戶主檔、聯絡人、地址與付款安排",
      "保留報價、訂單、收款與往來紀錄",
      "支援客戶分級、價格層級與重點客戶標記",
    ],
    sopFit: [
      "客戶分級帶出價格安排",
      "報價與訂單共用同一客戶資料",
      "業務與財務共用欠款狀態",
    ],
    icon: "ri-user-heart-line",
    accent: "sunset",
  },
  {
    id: "vendors",
    label: "供應商管理",
    short: "供應商、成本、交期與往來紀錄集中管理",
    title: "當同一件貨有幾個供應來源，採購判斷就不應只靠同事記憶",
    description:
      "供應商管理模組會把供應商主檔、歷史價格、交期表現和往來紀錄整理在一起，讓採購部在詢價、落單和補貨時有更清晰的依據。",
    bullets: [
      "建立供應商主檔、聯絡資料、付款安排與往來記錄",
      "保留歷史報價、採購價格與交期表現，方便比較供應來源",
      "讓採購、收貨與財務看到同一份供應商資料",
    ],
    sopFit: [
      "不同供應商用不同付款條件",
      "比較交期與歷史成本再決定下單",
      "收貨與採購單共用供應商資料",
    ],
    icon: "ri-truck-line",
    accent: "amber",
  },
  {
    id: "hr",
    label: "人事與薪酬",
    short: "人員資料、考勤、薪酬與部門成本集中處理",
    title: "當人工成本和營運分開看，人事資料就很難真正支援管理決策",
    description:
      "人事與薪酬模組會把員工資料、考勤、假期、津貼、薪酬和部門成本放回同一套流程，讓 HR、財務和管理層看到一致的人員與成本資料。",
    bullets: [
      "管理員工檔案、職級、部門、考勤、假期與薪酬結構",
      "支援薪酬計算與部門成本整理，方便回看到營運層面",
      "適合希望把 HR 資料與財務流程接回同一系統的公司",
    ],
    sopFit: [
      "考勤資料帶入薪酬處理",
      "津貼與人工成本回看到部門",
      "HR 與財務共用同一員工資料",
    ],
    icon: "ri-team-line",
    accent: "sky",
  },
  {
    id: "bi",
    label: "報表分析",
    short: "用同一套數據看營收、庫存、採購與現金流",
    title: "當數據來自同一套流程，報表才不只是整理，而是幫你更快作判斷",
    description:
      "報表分析模組的價值不在於報表數量，而在於把銷售、採購、庫存、收付款和成本資料拉回同一視角，讓管理層更快看到異常與趨勢。",
    bullets: [
      "整合營收、庫存、採購、收付款與部門成本的管理視圖",
      "協助追蹤異常訂單、低庫存、欠款與採購風險",
      "讓經營者用同一套數據做補貨、追數與資源配置判斷",
    ],
    sopFit: [
      "一頁看清今日待跟進重點",
      "用同一套數據追營收與現金流",
      "異常先在 dashboard 發現再跟進",
    ],
    icon: "ri-bar-chart-box-line",
    accent: "violet",
  },
] as const;

const valuePoints = [
  {
    title: "一個方案價錢",
    text: "先把範圍講清楚，不用逐個功能、逐張報表、逐段流程慢慢加價。",
    icon: "ri-price-tag-3-line",
  },
  {
    title: "包常用 SOP 流程",
    text: "按你公司日常的報價、批核、採購、出貨、收款做法去整理和配置。",
    icon: "ri-git-branch-line",
  },
  {
    title: "不是由零開發",
    text: "用成熟 ERP 底盤承接流程，所以更容易控制時間、成本和上線風險。",
    icon: "ri-stack-line",
  },
] as const;

const includedItems = [
  "了解現有流程與重點角色分工",
  "整理常用 SOP 與例外情況",
  "配置欄位、單據、狀態與基本權限",
  "包含部分按你公司日常流程而做的定制功能與操作調整",
  "串起報價、落單、採購、庫存、收付款等常用流程",
  "按日常操作安排演示與上線說明",
  "預留後續擴展到 WMS / TMS 的空間",
] as const;

const notIncludedItems = [
  "完全由零開發的新系統",
  "極深度、跨多部門的大型客製化專案",
  "大量全新資料模型或複雜外部平台整合",
  "未經確認範圍的無上限修改要求",
] as const;

const sopExamples = [
  {
    title: "報價到落單",
    flow: "報價批核後自動轉銷售單",
    detail:
      "不用再由業務和營運重覆輸入，亦可按客戶帶出價格、信用額和付款條件。",
  },
  {
    title: "採購與補貨",
    flow: "按庫存水平和需求建立請購 / 採購",
    detail:
      "把你公司常見的補貨方式和負責人節點放進系統，而不是要求同事改用陌生流程。",
  },
  {
    title: "收款與對數",
    flow: "用 reference 對回發票與應收",
    detail:
      "適合本地公司常見的收款、對帳和追數做法，減少收到錢但唔知對邊張單。",
  },
  {
    title: "內部批核",
    flow: "按金額、部門或角色做不同批核",
    detail: "例如採購金額超過某門檻先交主管批，讓 SOP 反映你公司本身管理習慣。",
  },
] as const;

const rolloutSteps = [
  {
    step: "01",
    title: "先看你現在怎樣做",
    text: "不是先開功能表，而是先了解你現時如何報價、落單、採購、出貨、收款和對數。",
  },
  {
    step: "02",
    title: "整理成可落地 SOP",
    text: "把哪些流程可以直接套用、哪些欄位和批核需要配置，先講清楚範圍。",
  },
  {
    step: "03",
    title: "用成熟底盤配置",
    text: "在既有 ERP 架構內完成常用配置，避免走向高成本、長週期的由零開發。",
  },
  {
    step: "04",
    title: "先跑順最常用流程",
    text: "先讓一條最重要的流程穩定落地，再按需要擴展其他部門、模組或整合。",
  },
] as const;

const fitCompanies = [
  "現時主要靠 Excel、WhatsApp 和人手搬資料",
  "流程已經成形，但想用更清晰預算導入系統",
  "想要系統配合公司做法，而不是全公司遷就軟件",
  "希望先落地常用 SOP，再逐步擴展更多功能",
] as const;

const notFitCompanies = [
  "預期任何流程都可以無限制即時追加",
  "需要完全重新發明一套獨有系統架構",
  "短時間內要處理大量高度複雜 custom 開發",
  "未準備好梳理現有做法，就想直接跳到開發階段",
] as const;

const faqs = [
  {
    q: "全包方案是否代表什麼都包？",
    a: "不是無上限，而是先把導入範圍、常用 SOP 和交付內容講清楚，讓你容易預算，也避免後期不停加項。",
  },
  {
    q: "如果我們流程和別人不一樣怎麼辦？",
    a: "這正是重點。我們不只是展示標準功能，而是先了解你現時做法，並在方案內包括部分按你公司日常流程而做的定制功能與配置調整，讓系統更貼近實際運作。",
  },
  {
    q: "是否一定要一次上齊全部模組？",
    a: "不需要。建議先上最常用、最混亂或最值得整理的一段流程，穩定後再擴展。",
  },
  {
    q: "你們做過什麼行業？",
    a: "我們接觸過的業務類型包括旅遊巴、零售店、貿易、批發、倉儲物流等。重點不只是行業名稱，而是你公司日常怎樣報價、落單、採購、出貨、收款和對數，我們會按這些實際做法去整理流程。",
  },
  {
    q: "如果我們現在主要靠 Excel 和 WhatsApp，可否開始？",
    a: "可以，這正是很多中小企開始導入 ERP 的情況。我們會先看你現時最常用的流程，把重覆輸入、靠人記憶和容易出錯的部分先整理好，再逐步帶進系統。",
  },
  {
    q: "系統是否適合中小企，不會太複雜嗎？",
    a: "我們這套方案本身就是朝中小企日常運作去設計，重點是先簡化最常用流程，而不是一開始就把所有功能全部打開。這樣同事較容易上手，管理層也更容易看到實際效果。",
  },
  {
    q: "導入前是否一定要先整理好所有流程？",
    a: "不需要先自己全部整理完成。很多公司其實只大概知道現時做法，但未必已經完整寫成 SOP。我們可以先和你一起梳理最常用的一段流程，再逐步清楚其他部分。",
  },
] as const;

export default function ERPPage() {
  const [activeModule, setActiveModule] = useState(0);
  const { t } = useTranslation();
  const currentModule = erpModules[activeModule];
  const theme = moduleThemes[currentModule.accent];
  const pageTitle = "ERP 企業管理系統 | 一個方案價錢配合公司 SOP | AUTOCHAIN";
  const pageDescription =
    "AutoERP以一個清晰方案價錢，配合你公司日常 SOP 流程，協助企業整理報價、落單、採購、出貨、收款與對數。";

  const openWhatsApp = (zhMessage: string, enMessage: string) => {
    const message = encodeURIComponent(
      t("nav.language") === "中" ? zhMessage : enMessage,
    );

    window.open(`https://wa.me/85294252903?text=${message}`, "_blank");
  };

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords="ERP 系統, 企業管理系統, 香港 ERP, SOP 流程, ERP 定制, 財務會計系統, 採購管理, 銷售管理, 庫存管理, AUTOCHAIN"
        ogTitle={pageTitle}
        ogDescription={pageDescription}
        ogImage="https://autochain.com.hk/erp-og-image.png"
        canonicalUrl="/erp"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "AutoERP企業管理系統",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web Browser",
            description: pageDescription,
            url: "https://autochain.com.hk/erp",
            provider: {
              "@type": "Organization",
              name: "AUTOCHAIN TECHNOLOGY",
            },
            featureList: erpModules.map((module) => module.label),
            applicationSubCategory: [
              "Hong Kong ERP",
              "SOP Workflow Configuration",
              "Finance and Accounting",
              "Purchasing and Inventory",
              "Sales and Order Management",
            ],
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首頁",
                item: "https://autochain.com.hk/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "ERP 企業管理系統",
                item: "https://autochain.com.hk/erp",
              },
            ],
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            })),
          }),
        }}
      />

      <Header />

      <section className="relative overflow-hidden bg-[#fff7ef]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.18),_transparent_28%),radial-gradient(circle_at_80%_0%,_rgba(45,212,191,0.18),_transparent_24%),linear-gradient(180deg,_rgba(255,247,239,1),_rgba(255,255,255,1))]" />
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-24 sm:px-6 lg:px-8 lg:pb-24 lg:pt-32">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm">
                <i className="ri-lightbulb-flash-line"></i>
                ERP 企業管理系統
              </div>
              <h1 className="mt-6 text-4xl font-bold leading-tight text-slate-900 md:text-6xl">
                不是賣一堆功能，
                <span className="block bg-gradient-to-r from-orange-600 via-amber-500 to-teal-500 bg-clip-text text-transparent">
                  而是一個清晰方案價錢，配合你公司日常 SOP 流程
                </span>
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600 md:text-xl">
                AutoERP不是叫你遷就一套生硬功能表，而是以成熟 ERP
                底盤，配合你公司現有的報價、落單、採購、出貨、收款與對數做法，
                整理成清晰可落地的日常流程，讓系統真正用得起來。
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <div className="rounded-3xl border border-orange-100 bg-white px-5 py-4 shadow-sm">
                  <div className="text-2xl font-bold text-slate-900">清晰</div>
                  <div className="mt-1 text-sm text-slate-500">
                    方案價錢與範圍
                  </div>
                </div>
                <div className="rounded-3xl border border-teal-100 bg-white px-5 py-4 shadow-sm">
                  <div className="text-2xl font-bold text-slate-900">常用</div>
                  <div className="mt-1 text-sm text-slate-500">
                    SOP 流程一起做
                  </div>
                </div>
                <div className="rounded-3xl border border-amber-100 bg-white px-5 py-4 shadow-sm">
                  <div className="text-2xl font-bold text-slate-900">逐步</div>
                  <div className="mt-1 text-sm text-slate-500">
                    先跑順再擴展
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Button
                  variant="primary"
                  size="lg"
                  className="px-8 py-4 text-lg"
                  onClick={() =>
                    openWhatsApp(
                      "您好！我想了解 AutoERP的全包方案，看看是否適合我們公司的 SOP。",
                      "Hello! I would like to learn more about AUTOCHAIN's ERP packaged offer and whether it fits our company's SOP.",
                    )
                  }
                >
                  了解全包方案
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-slate-300 px-8 py-4 text-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  onClick={() =>
                    openWhatsApp(
                      "您好！我想先講我們公司現有流程，看看 AutoERP可怎樣配合。",
                      "Hello! I would like to share our current workflow first and see how AutoERPcan fit it.",
                    )
                  }
                >
                  先講現有流程
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-x-10 top-0 h-24 rounded-full bg-orange-100/60 blur-3xl" />
              <div className="relative overflow-hidden rounded-[30px] border border-stone-200/80 bg-white/95 shadow-[0_28px_70px_-42px_rgba(15,23,42,0.28)]">
                <div className="border-b border-stone-200/80 px-6 py-6 sm:px-7">
                  <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-orange-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                    AutoERP
                  </div>
                  <div className="mt-4 max-w-md text-2xl font-semibold leading-tight text-slate-900 sm:text-[2rem]">
                    這套系統是專為中小企而設
                  </div>
                  <p className="mt-3 max-w-lg text-sm leading-7 text-slate-600 sm:text-[15px]">
                    不是把流程做得更複雜，而是按中小企日常最常用的做法，先幫你整理和簡化報價、落單、採購、出貨與收款流程。
                  </p>
                </div>

                <div className="px-6 py-4 sm:px-7 sm:py-5">
                  <div className="space-y-3">
                    {valuePoints.map((point, index) => (
                      <div
                        key={point.title}
                        className="flex items-start gap-4 rounded-[22px] border border-stone-200/80 bg-stone-50/70 px-4 py-4"
                      >
                        <div className="pt-1 text-xs font-semibold tracking-[0.2em] text-stone-400">
                          0{index + 1}
                        </div>
                        {/* <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-lg text-orange-600 shadow-sm ring-1 ring-stone-200/80">
                          <i className={point.icon}></i>
                        </div> */}
                        <div className="min-w-0">
                          <div className="text-base font-semibold text-slate-900 sm:text-lg">
                            {point.title}
                          </div>
                          <p className="mt-1.5 text-sm leading-7 text-slate-600">
                            {point.text}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 flex items-center gap-3 rounded-[22px] bg-slate-900 px-4 py-4 text-sm text-white/80">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-orange-300">
                      <i className="ri-arrow-right-up-line"></i>
                    </div>
                    <p className="leading-6">
                      先把最常用的流程簡化，再按實際需要逐步擴展。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700">
              全包範圍
            </span>
            <h2 className="mt-5 text-3xl font-bold text-slate-900 md:text-5xl">
              先講清楚方案包括什麼，
              <span className="block text-orange-600">
                導入才更容易預算和落地
              </span>
            </h2>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <div className="rounded-[32px] border border-emerald-100 bg-[#f3fbf8] p-8 shadow-lg shadow-emerald-100/50">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500 text-2xl text-white">
                  <i className="ri-check-line"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">
                    Included
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900">
                    方案內通常包括
                  </div>
                </div>
              </div>
              <div className="mt-6 space-y-3">
                {includedItems.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-white px-4 py-4 text-slate-700"
                  >
                    <i className="ri-arrow-right-up-line mt-1 text-emerald-600"></i>
                    <span className="leading-7">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[32px] border border-slate-200 bg-slate-50 p-8 shadow-lg shadow-slate-200/60">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-2xl text-white">
                  <i className="ri-information-line"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Scope Control
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900">
                    需要另定範圍的情況
                  </div>
                </div>
              </div>
              <p className="mt-6 leading-8 text-slate-600">
                全包方案不等於無上限開發，而是先界定常用流程、交付範圍與導入方式。
                如果需求屬於大型深度定制，我們會先另外評估，再建議最合適做法。
              </p>
              <div className="mt-6 space-y-3">
                {notIncludedItems.map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-slate-700"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#fff9f3] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex rounded-full border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm">
              SOP 例子
            </span>
            <h2 className="mt-5 text-3xl font-bold text-slate-900 md:text-5xl">
              常見公司日常 SOP，
              <span className="block text-orange-600">
                可以怎樣放進同一套 ERP 流程
              </span>
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {sopExamples.map((example) => (
              <div
                key={example.title}
                className="rounded-[30px] border border-orange-100 bg-white p-7 shadow-lg shadow-orange-100/50"
              >
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-500">
                  {example.title}
                </div>
                <h3 className="mt-3 text-2xl font-semibold text-slate-900">
                  {example.flow}
                </h3>
                <p className="mt-4 leading-8 text-slate-600">
                  {example.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <span className="inline-flex rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
              常見管理範圍
            </span>
            <h2 className="mt-5 text-3xl font-bold text-slate-900 md:text-5xl">
              方案先講清楚，
              <span className="block text-orange-600">
                模組再用來承接你公司日常 SOP
              </span>
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              下面這些不是獨立功能表，而是 most companies 最常會用到的管理範圍。
              你公司現有流程，通常會落在其中幾個模組一起配合。
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-10 xl:grid-cols-[1.02fr_0.98fr] xl:items-start">
            <div className="grid grid-cols-2 gap-x-5 gap-y-7 md:grid-cols-3">
              {erpModules.map((module, index) => {
                const isActive = index === activeModule;
                const moduleTheme = moduleThemes[module.accent];

                return (
                  <button
                    key={module.id}
                    type="button"
                    onClick={() => setActiveModule(index)}
                    className="group relative pt-3 text-left"
                  >
                    <div
                      className={`absolute left-4 right-4 top-0 h-5 rounded-full bg-gradient-to-r ${
                        isActive
                          ? moduleTheme.panel
                          : "from-orange-500 via-amber-400 to-teal-400"
                      }`}
                    />
                    <div
                      className={`relative rounded-[28px] border bg-white px-5 pb-6 pt-7 transition-all duration-200 ${
                        isActive
                          ? `${moduleTheme.border} ring-2 ${moduleTheme.ring} -translate-y-1 shadow-xl ${moduleTheme.glow}`
                          : "border-slate-200 shadow-sm hover:-translate-y-1 hover:shadow-lg"
                      }`}
                    >
                      <div
                        className={`flex h-14 w-14 items-center justify-center rounded-2xl text-2xl ${
                          isActive
                            ? moduleTheme.chip
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        <i className={module.icon}></i>
                      </div>
                      <div className="mt-5 text-xl font-semibold text-slate-900">
                        {module.label}
                      </div>
                      <div className="mt-3 border-t border-dashed border-slate-300 pt-3 text-sm leading-6 text-slate-500">
                        {module.short}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-2xl shadow-orange-100/50">
              <div
                className={`bg-gradient-to-r ${theme.panel} px-8 py-8 text-white`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-white/70">
                      AutoERP
                    </div>
                    <h3 className="mt-3 text-4xl font-bold leading-tight">
                      {currentModule.label}
                    </h3>
                  </div>
                  <div className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur">
                    SOP Fit
                  </div>
                </div>
                <p className="mt-3 max-w-3xl text-lg leading-8 text-white/90">
                  {currentModule.title}
                </p>
              </div>

              <div className="px-8 py-8">
                <div className="rounded-3xl bg-slate-50 px-6 py-5 text-slate-700 leading-8">
                  {currentModule.description}
                </div>

                <div className="mt-8">
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    這個範圍通常會處理什麼
                  </div>
                  <div className="mt-4 space-y-3">
                    {currentModule.bullets.map((bullet) => (
                      <div
                        key={bullet}
                        className={`flex items-start gap-3 rounded-2xl ${theme.soft} p-4`}
                      >
                        <i
                          className={`ri-check-double-line mt-1 text-lg ${theme.text}`}
                        ></i>
                        <p className="text-slate-700 leading-7">{bullet}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8 rounded-[28px] border border-orange-100 bg-gradient-to-r from-orange-50 via-white to-teal-50 p-6">
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    常見 SOP 配合方式
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {currentModule.sopFit.map((item) => (
                      <div
                        key={item}
                        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <Button
                    variant="primary"
                    onClick={() =>
                      openWhatsApp(
                        `您好！我想了解 AutoERP如何配合我們公司在「${currentModule.label}」方面的流程。`,
                        `Hello! I would like to understand how AutoERPcan support our workflow for ${currentModule.label}.`,
                      )
                    }
                  >
                    了解這個範圍
                  </Button>
                  <Button
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                    onClick={() =>
                      openWhatsApp(
                        "您好！我想了解我們公司整體流程適合由哪些 ERP 模組開始。",
                        "Hello! I would like to understand which ERP areas our company should start with.",
                      )
                    }
                  >
                    先看導入次序
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.92fr_1.08fr]">
            <div>
              <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-orange-200">
                導入方式
              </span>
              <h2 className="mt-5 text-3xl font-bold leading-tight md:text-5xl">
                由現有做法開始，
                <span className="block text-orange-300">
                  逐步整理成可落地的系統流程
                </span>
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-300">
                我們會先了解你公司現時怎樣報價、落單、採購、出貨、收款和對數，
                再決定哪些流程可以直接配置，哪些需要另外處理，讓導入更貼近實際營運。
              </p>
            </div>

            <div className="grid gap-4">
              {rolloutSteps.map((step) => (
                <div
                  key={step.step}
                  className="rounded-[28px] border border-white/10 bg-white/5 p-6"
                >
                  <div className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-200">
                    Step {step.step}
                  </div>
                  <h3 className="mt-3 text-2xl font-semibold">{step.title}</h3>
                  <p className="mt-3 leading-8 text-slate-300">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
              適合度
            </span>
            <h2 className="mt-5 text-3xl font-bold text-slate-900 md:text-4xl">
              哪類公司較適合以這種方式導入 ERP
            </h2>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <div className="rounded-[32px] border border-teal-100 bg-[#f2fbfb] p-8 shadow-lg shadow-teal-100/50">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-teal-700">
                Better Fit
              </div>
              <h3 className="mt-3 text-2xl font-semibold text-slate-900">
                較適合這個方案的公司
              </h3>
              <div className="mt-6 space-y-3">
                {fitCompanies.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 rounded-2xl border border-teal-100 bg-white px-4 py-4 text-slate-700"
                  >
                    <i className="ri-checkbox-circle-line mt-1 text-teal-600"></i>
                    <span className="leading-7">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[32px] border border-rose-100 bg-[#fff6f6] p-8 shadow-lg shadow-rose-100/50">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-rose-700">
                Less Suitable
              </div>
              <h3 className="mt-3 text-2xl font-semibold text-slate-900">
                需要先另作評估的情況
              </h3>
              <div className="mt-6 space-y-3">
                {notFitCompanies.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 rounded-2xl border border-rose-100 bg-white px-4 py-4 text-slate-700"
                  >
                    <i className="ri-subtract-line mt-1 text-rose-600"></i>
                    <span className="leading-7">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#fff8f1] py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex rounded-full bg-white px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm">
              常見問題
            </span>
            <h2 className="mt-5 text-3xl font-bold text-slate-900 md:text-4xl">
              先把方案範圍和導入方式講清楚
            </h2>
          </div>

          <div className="mt-10 space-y-4">
            {faqs.map((item) => (
              <div
                key={item.q}
                className="rounded-[28px] border border-orange-100 bg-white p-6 shadow-sm"
              >
                <h3 className="text-xl font-semibold text-slate-900">
                  {item.q}
                </h3>
                <p className="mt-3 leading-8 text-slate-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-orange-600 via-amber-500 to-teal-500 py-20 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <div className="text-sm font-semibold uppercase tracking-[0.2em] text-white/80">
            AutoERP
          </div>
          <h2 className="mt-4 text-4xl font-bold">
            不如先講你公司現時怎樣運作，
            <span className="block">再看看是否適合用一個清晰方案價錢落地</span>
          </h2>
          <p className="mt-6 text-xl leading-8 text-white/90">
            如果你正在由 Excel 和分散工具過渡到更完整的企業管理方式，
            可以先和我們講現時流程，我們再建議最適合的導入範圍和次序。
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Button
              variant="outline"
              size="lg"
              className="border-white bg-white px-8 py-4 text-lg text-orange-600 hover:bg-orange-50"
              onClick={() =>
                openWhatsApp(
                  "您好！我想先講我們公司現有 SOP，看看是否適合 AutoERP的全包方案。",
                  "Hello! I would like to share our current SOP first and see whether AUTOCHAIN's ERP packaged offer is a fit.",
                )
              }
            >
              先講公司 SOP
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="bg-slate-950 px-8 py-4 text-lg hover:bg-slate-900"
              onClick={() =>
                openWhatsApp(
                  "您好！我想了解 AutoERP的方案範圍與導入方式。",
                  "Hello! I would like to understand AutoERP's project scope and rollout approach.",
                )
              }
            >
              了解方案範圍
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
